"use client"

//region Imports
import React, {useRef, useState, useEffect} from 'react';
import {ToastContainer} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import _ from 'lodash';

import {cn} from "@isudio/utils";

import type {BreadcrumbItems, LinkItem} from "../";
import {Header, Footer, Panel, useOpenComponent, useDialogController} from "../../";
import {FadeTransition} from "../../../constants";
//endregion Imports

//region Types & Interfaces
interface LayoutProps {
  className?: string;
  hfPosition?: "sticky" | "fixed";
  videoPage?: boolean;
  showHeader?: boolean;
  showFooter?: boolean;
  icon?: string;
  image?: string;
  title?: string;
  breadCrumbs?: BreadcrumbItems[];
  links?: LinkItem[];
  showPanel?: boolean;
  panelCaption?: string;
  enableScrollTracking?: boolean;
  onScroll?: (
    isOverFlow: boolean,
    scrollTop: number,
    scrollProgress: number,
    containerRef: React.RefObject<HTMLDivElement>
  ) => void;
  children: React.ReactNode;
}
//endregion Types & Interfaces

const General = React.forwardRef<Element, LayoutProps>(
  ({
     className = "",
     hfPosition = "sticky",
     videoPage = false,
     showHeader = true,
     showFooter = true,
     icon,
     image,
     title,
     breadCrumbs,
     links,
     showPanel = true,
     panelCaption,
     enableScrollTracking = false,
     onScroll,
     children
   }
  ) => {
    //region Variables
    const containerRef = useRef<HTMLDivElement>(null);

    const panelRef = useRef<HTMLDivElement>(null);
    const [headerHeight, setHeaderHeight] = useState(0);
    const [footerHeight, setFooterHeight] = useState(0);
    const openComponent = useOpenComponent();
    const {isDialogOpen} = useDialogController();
    //endregion Variables

    //region Methods & Event Handlers
    const openPrivacyPolicy = async () => {
      openComponent("privacyPolicy");
    };

    const openTermsOfUse = async () => {
      openComponent("termsOfUse");
    };

    if (_.isNothing(links)) {
      links = [
        {
          href: "#",
          label: "Privacy Policy",
          handler: openPrivacyPolicy
        },
        {
          href: "#",
          label: "Terms of Use",
          handler: openTermsOfUse
        }
      ];
    }

    const _onScroll = () => {
      const el = containerRef.current;
      if (!el) return;

      const top = el.scrollTop;
      const max = el.scrollHeight - el.clientHeight;

      onScroll?.(
        el.scrollHeight > el.clientHeight,
        top,
        max > 0 ? top / max : 0,
        containerRef
      );
    };
    //endregion Methods & Event Handlers

    //region Effects
    useEffect(() => {
      if (!enableScrollTracking) return;

      const el = containerRef.current;
      if (!el) return;

      el.addEventListener("scroll", _onScroll);
      _onScroll();

      return () => el.removeEventListener("scroll", _onScroll);
    }, [enableScrollTracking]);

    useEffect(() => {
      const checkOverflow = () => {
        if (containerRef.current) {
          const el = containerRef.current;
          const isOverflow = el.scrollHeight > el.clientHeight;
          const scrollProgress = el.scrollHeight > el.clientHeight
            ? el.scrollTop / (el.scrollHeight - el.clientHeight)
            : 0;

          onScroll?.(isOverflow, el.scrollTop, scrollProgress, containerRef);
        }
      };

      checkOverflow();

      const resizeObserver = new ResizeObserver(checkOverflow);
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }

      return () => resizeObserver.disconnect();
    }, [onScroll]);
    //endregion Effects

    //region HTML Elements
    const header = (
      <div>
        <Header
          position={hfPosition}
          visible={showHeader}
          title={title}
          image={image}
          icon={icon}
          breadCrumbs={breadCrumbs}
          showRightNav={false}
          onHeightChange={setHeaderHeight}
        />
      </div>
    );

    const panel = (
      <Panel
        panelRef={panelRef}
        panelCaption={panelCaption}
        visible={showPanel}
      />
    );

    const content = (
      <div
        ref={containerRef}
        className={cn(`
          content flex flex-1 flex-col h-auto items-center overflow-y-auto 
          bg-[radial-gradient(circle,#ffffff_0%,#d6d6d6_100%)] 
          dark:bg-[radial-gradient(circle,#030b11b8_0%,#030b11d8_100%)] 
          scrollbar-thin scrollbar-track-rounded-full 
          scrollbar-track-dim-white dark:scrollbar-track-transparent 
          scrollbar-thumb-custom-blue-600 
          scrollbar-hover:scrollbar-thumb-custom-blue-300 
          scrollbar-active:scrollbar-thumb-custom-blue-300
        `, className)}
        style={{
          paddingTop: `${videoPage ? 0 : headerHeight}px`,
          paddingBottom: `${footerHeight}px`
        }}
      >
        {children}
        {panel}
      </div>
    );

    const toastContainer = (
      <ToastContainer
        position="bottom-center"
        autoClose={3000}
        newestOnTop={true}
        hideProgressBar={true}
        closeButton={false}
        closeOnClick={true}
        pauseOnHover={true}
        draggable={false}
        theme="dark"
        transition={FadeTransition}
        className="absolute left-0 right-0 bottom-0 z-9999 pointer-events-none"
        toastClassName="mx-auto pointer-events-auto flex !m-0 items-center gap-2 rounded-xl shadow-lg border-none transition-all duration-200"
      />
    );

    const footer = (
      <div>
        <Footer
          position={hfPosition}
          visible={showFooter}
          links={links}
          onHeightChange={setFooterHeight}
        />
      </div>
    );
    //endregion HTML Elements

    return (
      <div className="flex flex-col w-screen h-screen overflow-hidden relative">
        {header}

        {content}

        {!isDialogOpen &&
          toastContainer
        }

        {footer}
      </div>
    );
  }
);

General.displayName = "General";
export {General};